import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  REGION_ORDER,
  type CountryMetadata,
} from "../data/countries";
import { loadState } from "../state/storage";
import { isRegionCleared } from "../state/stats";

const MIXED_REGION = "Mixed (cleared regions)";

const progressLine = (
  countries: CountryMetadata[],
  knownIsos: Set<string>,
  cleared: boolean,
): string => {
  if (cleared) return `Cleared — ${countries.length} countries`;
  const known = countries.filter((c) => knownIsos.has(c.iso)).length;
  return `${known} / ${countries.length} answered correctly`;
};

export const mountRegionPicker = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  // Reset globe to neutral exploratory state.
  ctx.globe.setTargetIso(null);
  ctx.globe.setHoverEnabled(true);

  root.classList.add("view-region-picker");

  const card = document.createElement("section");
  card.className = "picker-card";
  card.innerHTML = `
    <h1>Geo Gauntlet</h1>
    <p class="picker-subtitle">Pick a region to practice.</p>
    <p class="picker-loading">Loading…</p>
  `;
  root.appendChild(card);

  const table = await loadCountries();
  const state = loadState();
  const grouped = groupByRegion(table);

  const knownIsos = new Set<string>();
  for (const [iso, stats] of Object.entries(state.stats)) {
    if (stats.totalCorrect > 0) knownIsos.add(iso);
  }

  const clearedRegions = REGION_ORDER.filter((r) => {
    const countries = grouped[r];
    if (!countries) return false;
    return isRegionCleared(
      state,
      countries.map((c) => c.iso),
    );
  });

  // Render
  card.innerHTML = `
    <h1>Geo Gauntlet</h1>
    <p class="picker-subtitle">Pick a region to practice.</p>
    <ul class="region-list" role="list"></ul>
    <div class="picker-footer">
      <button type="button" class="picker-link picker-arcade">Arcade</button>
      <button type="button" class="picker-link picker-heatmap">Mastery map</button>
      <button type="button" class="picker-link picker-browse">Browse</button>
      <button type="button" class="picker-link picker-settings">Settings</button>
    </div>
    <button type="button" class="picker-hide">Hide menu &amp; explore the globe</button>
  `;
  const list = card.querySelector(".region-list") as HTMLUListElement;
  const hideBtn = card.querySelector(".picker-hide") as HTMLButtonElement;
  const settingsBtn = card.querySelector(".picker-settings") as HTMLButtonElement;
  const heatmapBtn = card.querySelector(".picker-heatmap") as HTMLButtonElement;
  const arcadeBtn = card.querySelector(".picker-arcade") as HTMLButtonElement;
  const browseBtn = card.querySelector(".picker-browse") as HTMLButtonElement;

  // Floating "show menu" button — visible only when the picker card is
  // hidden. Plain hamburger icon + label.
  const showBtn = document.createElement("button");
  showBtn.type = "button";
  showBtn.className = "picker-show";
  showBtn.setAttribute("aria-label", "Show menu");
  showBtn.hidden = true;
  showBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            d="M4 7h16M4 12h16M4 17h16"/>
    </svg>
    <span>Menu</span>
  `;
  root.appendChild(showBtn);

  const setMenuVisible = (visible: boolean): void => {
    card.hidden = !visible;
    showBtn.hidden = visible;
  };
  const onHide = (): void => setMenuVisible(false);
  const onShow = (): void => setMenuVisible(true);
  const onSettings = (): void => ctx.nav.goSettings();
  const onHeatmap = (): void => ctx.nav.goHeatmap();
  const onArcade = (): void => ctx.nav.goArcade();
  const onBrowse = (): void => ctx.nav.goBrowse();
  hideBtn.addEventListener("click", onHide);
  showBtn.addEventListener("click", onShow);
  settingsBtn.addEventListener("click", onSettings);
  heatmapBtn.addEventListener("click", onHeatmap);
  arcadeBtn.addEventListener("click", onArcade);
  browseBtn.addEventListener("click", onBrowse);

  const items: { el: HTMLButtonElement; cleanup: () => void }[] = [];

  for (const region of REGION_ORDER) {
    const countries = grouped[region] ?? [];
    if (countries.length === 0) continue;
    const cleared = clearedRegions.includes(region);
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.className = "region-btn" + (cleared ? " is-cleared" : "");
    btn.innerHTML = `
      <span class="region-name">${region}</span>
      <span class="region-progress">${progressLine(countries, knownIsos, cleared)}</span>
    `;
    const handler = (): void => {
      ctx.nav.goLearn({ region });
    };
    btn.addEventListener("click", handler);
    li.appendChild(btn);
    list.appendChild(li);
    items.push({ el: btn, cleanup: () => btn.removeEventListener("click", handler) });
  }

  if (clearedRegions.length > 0) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.className = "region-btn region-btn-mixed";
    const mixedCountries = clearedRegions.flatMap((r) => grouped[r] ?? []);
    btn.innerHTML = `
      <span class="region-name">${MIXED_REGION}</span>
      <span class="region-progress">${mixedCountries.length} countries from cleared regions</span>
    `;
    const handler = (): void => {
      ctx.nav.goLearn({ region: MIXED_REGION });
    };
    btn.addEventListener("click", handler);
    li.appendChild(btn);
    list.appendChild(li);
    items.push({ el: btn, cleanup: () => btn.removeEventListener("click", handler) });
  }

  return () => {
    for (const { cleanup } of items) cleanup();
    hideBtn.removeEventListener("click", onHide);
    showBtn.removeEventListener("click", onShow);
    settingsBtn.removeEventListener("click", onSettings);
    heatmapBtn.removeEventListener("click", onHeatmap);
    arcadeBtn.removeEventListener("click", onArcade);
    browseBtn.removeEventListener("click", onBrowse);
    showBtn.remove();
    root.classList.remove("view-region-picker");
  };
};

export const isMixedRegion = (region: string): boolean => region === MIXED_REGION;

export const getCountriesForRegion = (
  region: string,
  table: ReturnType<typeof groupByRegion>,
  state: ReturnType<typeof loadState>,
): CountryMetadata[] => {
  if (isMixedRegion(region)) {
    const out: CountryMetadata[] = [];
    for (const r of REGION_ORDER) {
      const countries = table[r];
      if (!countries) continue;
      if (
        isRegionCleared(
          state,
          countries.map((c) => c.iso),
        )
      ) {
        out.push(...countries);
      }
    }
    return out;
  }
  return table[region] ?? [];
};
